import { useForm, type UseFormReturn } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import { actions, Document } from '@kanda-libs/ks-frontend-services';
import { URLS } from 'config';
import { useApiError, useAppDispatch } from 'hooks';
import { useEnquiryContext } from 'pages/Enquiry/context';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

export interface EnquiryQuoteUploadQuoteFormValues {
  document?: Document;
}

export interface EnquiryQuoteUploadQuoteFormHook {
  form: UseFormReturn<EnquiryQuoteUploadQuoteFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<EnquiryQuoteUploadQuoteFormValues>,
  ) => void;
  isSubmitting: boolean;
}

export default function useEnquiryQuoteUploadQuoteForm(): EnquiryQuoteUploadQuoteFormHook {
  const { id, enquiry } = useEnquiryContext();
  const { push } = useHistory();
  const dispatch = useAppDispatch();
  const onError = useApiError(
    'Unknown error uploading PDF - please try again or contact Ideal4Finance',
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const defaultValues = useMemo(() => {
    if (!enquiry) return {};
    const doc = enquiry?.enquiry_info?.quote;
    return {
      document: doc,
    };
  }, [enquiry]);

  const form = useForm<EnquiryQuoteUploadQuoteFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues,
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<EnquiryQuoteUploadQuoteFormValues>) => {
      if (!id) return;
      const fv = formValues as unknown as EnquiryQuoteUploadQuoteFormValues;
      const { document } = fv;
      if (!document) return;
      const noNewFile = Boolean(document?.id);
      if (noNewFile) {
        push(generatePath(URLS.enquiryQuoteConfirmDetails, { id }));
        return;
      }
      setIsSubmitting(true);
      dispatch(
        actions.postEnquiryQuote({
          params: { id },
          body: document,
          onError: (err) => {
            setIsSubmitting(false);
            onError(err);
          },
          onSuccess: () => {
            dispatch(
              actions.getInfoEnquiry({
                params: { id },
                forceReload: true,
                onError: () => {
                  window.location.href = window.location.href.replace(
                    /upload-quote/g,
                    'confirm-details',
                  );
                },
                onSuccess: () => {
                  setIsSubmitting(false);
                  push(generatePath(URLS.enquiryQuoteConfirmDetails, { id }));
                },
              }),
            );
          },
        }),
      );
    },
    [id, onError, dispatch, push],
  );

  return {
    form,
    onSubmit,
    isSubmitting,
  };
}
